import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { useCreateLead } from "@/hooks/useLeads";
import { useTrackEvent } from "@/hooks/useAnalytics";
import { useTurnstile } from "@/hooks/useTurnstile";
import {
  captchaState,
  formElapsedMs,
  HONEYPOT_INPUT_PROPS,
  leadSubmitErrorMessage,
} from "@/lib/leadIntake";
import { toast } from "sonner";
import { z } from "zod";
import { Photo } from "@/components/shared/Photo";

const PAINS = [
  "Preciso de site ou sistema",
  "Perco lead sem resposta",
  "Atendimento no caos",
  "Planilha demais",
  "Não sei os números",
] as const;

const GARANTIAS = [
  "Sem apresentação comercial nem proposta empurrada",
  "Eu digo se não vale a pena investir agora",
  "Você sai com o que priorizar, mesmo sem fechar",
  "Resposta em até 1 dia útil",
];

const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome muito longo"),
  phone: z.string().trim().min(8, "Informe seu WhatsApp").max(20, "Telefone muito longo"),
  email: z.string().trim().email("E-mail inválido").max(255, "E-mail muito longo"),
  message: z.string().trim().max(2000, "Mensagem muito longa"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const inputClass =
  "w-full h-[46px] px-[15px] rounded-xl border border-border bg-background text-[14px] text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors disabled:opacity-60";

const ContactNew = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [pains, setPains] = useState<string[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData | "pain", string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  // Time-trap: submit-lead quarantines forms sent faster than a human types.
  const mountedAt = useRef(Date.now());

  const createLead = useCreateLead();
  const trackEvent = useTrackEvent();
  const turnstile = useTurnstile();

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const togglePain = (pain: string) => {
    setPains((prev) => (prev.includes(pain) ? prev.filter((p) => p !== pain) : [...prev, pain]));
    if (errors.pain) setErrors((prev) => ({ ...prev, pain: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: typeof errors = {};
      result.error.errors.forEach((err) => {
        fieldErrors[err.path[0] as keyof ContactFormData] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    if (pains.length === 0 && result.data.message.length < 10) {
      setErrors({ pain: "Marque pelo menos uma opção ou conte em uma frase." });
      return;
    }

    const painLine = pains.length > 0 ? `Trava hoje: ${pains.join(", ")}` : "";
    const message = [painLine, result.data.message].filter(Boolean).join("\n\n");

    const captcha = captchaState(turnstile);
    if (captcha !== "ready") {
      toast.error(
        captcha === "failed"
          ? "A verificação de segurança não carregou. Recarregue a página ou chame no WhatsApp."
          : "Confirme a verificação de segurança.",
      );
      return;
    }

    try {
      await createLead.mutateAsync({
        name: result.data.name,
        email: result.data.email,
        phone: result.data.phone,
        message,
        project_type: "diagnostico",
        source: "contact_form",
        captchaToken: turnstile.token ?? undefined,
        honeypot,
        elapsedMs: formElapsedMs(mountedAt.current, Date.now()),
      });

      trackEvent.mutate({
        event_type: "form_submit",
        page_path: "/contato",
        metadata: { pain: pains },
      });

      setSubmitted(true);
      toast.success("Recebido! Respondo em até 1 dia útil.");
    } catch (err) {
      turnstile.reset();
      toast.error(
        leadSubmitErrorMessage(
          err instanceof Error ? err.message : null,
          "Erro ao enviar. Tente de novo ou chame no WhatsApp.",
        ),
      );
    }
  };

  return (
    <div className="mx-auto max-w-[1000px] px-8 pb-[90px] pt-[76px] max-[900px]:px-5">
      <div className="grid grid-cols-2 items-start gap-12 max-[900px]:grid-cols-1">
        <div>
          <h1 className="text-[40px] font-bold leading-[1.1] tracking-[-0.04em] [text-wrap:balance] max-[900px]:text-[38px]">
            Diagnóstico gratuito de 30 minutos
          </h1>
          <p className="mt-[18px] text-[16px] leading-[1.7] text-muted-foreground">
            Sem apresentação comercial. A gente olha seu negócio junto — site, sistema ou automação
            — e eu digo o que vale fazer primeiro, mesmo que você não feche nada comigo.
          </p>
          <div className="mt-[30px] flex flex-col gap-[15px]">
            {GARANTIAS.map((g) => (
              <div key={g} className="flex items-start gap-3">
                <span className="flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-primary">
                  ✓
                </span>
                <span className="text-[14.5px] leading-[1.55]">{g}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 aspect-[16/9] overflow-hidden rounded-2xl border border-border">
            <Photo id="aperto-de-mao" sizes="(max-width: 900px) 100vw, 440px" />
          </div>
          <div className="mt-4 rounded-2xl border border-border bg-muted px-[22px] py-5">
            <div className="text-[13px] font-bold">Prefere WhatsApp?</div>
            <p className="mt-[7px] text-[13.5px] leading-[1.6] text-muted-foreground">
              Chama direto em{" "}
              <a
                href="https://wa.me/5538988467524"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary-deep"
              >
                +55 (38) 98846-7524
              </a>{" "}
              — respondo em horário comercial.
            </p>
          </div>
        </div>

        {submitted ? (
          <div className="rounded-[20px] border border-border bg-card p-[30px] shadow-card">
            <div className="text-[17px] font-bold tracking-[-0.02em]">
              Recebido, {formData.name.split(" ")[0]}.
            </div>
            <p className="mt-2 text-[14.5px] leading-[1.6] text-muted-foreground">
              Respondo em até 1 dia útil pelo WhatsApp que você deixou. Se preferir adiantar, chama
              direto no{" "}
              <a
                href="https://wa.me/5538988467524"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                WhatsApp
              </a>
              .
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="relative flex flex-col gap-4 rounded-[20px] border border-border bg-card p-[30px] shadow-card"
          >
            <div className="flex flex-col gap-[7px]">
              <label
                htmlFor="contact-name"
                className="text-[12.5px] font-bold text-muted-foreground"
              >
                Seu nome
              </label>
              <input
                id="contact-name"
                className={`${inputClass} ${errors.name ? "border-destructive" : ""}`}
                placeholder="Como você se chama"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={createLead.isPending}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>

            <div className="flex flex-col gap-[7px]">
              <label
                htmlFor="contact-phone"
                className="text-[12.5px] font-bold text-muted-foreground"
              >
                WhatsApp
              </label>
              <input
                id="contact-phone"
                className={`${inputClass} ${errors.phone ? "border-destructive" : ""}`}
                placeholder="(00) 00000-0000"
                inputMode="tel"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                disabled={createLead.isPending}
              />
              {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
            </div>

            <div className="flex flex-col gap-[7px]">
              <label
                htmlFor="contact-email"
                className="text-[12.5px] font-bold text-muted-foreground"
              >
                E-mail
              </label>
              <input
                id="contact-email"
                type="email"
                className={`${inputClass} ${errors.email ? "border-destructive" : ""}`}
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                disabled={createLead.isPending}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>

            <div className="flex flex-col gap-[7px]">
              <p id="contact-pains-label" className="text-[12.5px] font-bold text-muted-foreground">
                O que mais trava hoje?
              </p>
              <div
                role="group"
                aria-labelledby="contact-pains-label"
                className="flex flex-wrap gap-[7px]"
              >
                {PAINS.map((pain) => {
                  const on = pains.includes(pain);
                  return (
                    <button
                      type="button"
                      key={pain}
                      onClick={() => togglePain(pain)}
                      aria-pressed={on}
                      className={`rounded-full border px-[13px] py-2 text-[12.5px] font-semibold transition-colors ${
                        on
                          ? "border-primary bg-accent text-primary"
                          : "border-border bg-background text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      {pain}
                    </button>
                  );
                })}
              </div>
              {errors.pain && <p className="text-xs text-destructive">{errors.pain}</p>}
            </div>

            <div className="flex flex-col gap-[7px]">
              <label
                htmlFor="contact-message"
                className="text-[12.5px] font-bold text-muted-foreground"
              >
                Conte em uma frase{" "}
                <span className="font-medium text-muted-foreground">(opcional)</span>
              </label>
              <textarea
                id="contact-message"
                className={`min-h-[88px] w-full resize-none rounded-xl border border-border bg-background px-[15px] py-[13px] text-[14px] leading-[1.6] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary ${
                  errors.message ? "border-destructive" : ""
                }`}
                placeholder="Ex: perdemos consultas porque ninguém confirma o agendamento…"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                disabled={createLead.isPending}
              />
              {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
            </div>

            <label
              htmlFor="lgpd-consent"
              className="flex cursor-pointer items-start gap-2.5 text-[11.5px] leading-[1.5] text-muted-foreground"
            >
              <Checkbox
                id="lgpd-consent"
                checked={lgpdConsent}
                onCheckedChange={(v) => setLgpdConsent(!!v)}
                className="mt-0.5"
              />
              <span>
                Concordo com o tratamento dos meus dados para fins de contato, conforme a{" "}
                <Link
                  to="/politica-de-privacidade"
                  className="text-primary underline hover:no-underline"
                >
                  Política de Privacidade
                </Link>
                .
              </span>
            </label>

            {/* Honeypot: off-screen (not display:none, which bots skip). Humans never fill it. */}
            <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
              <label htmlFor="contact-website">Site</label>
              <input
                id="contact-website"
                {...HONEYPOT_INPUT_PROPS}
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <div ref={turnstile.containerRef} />
              {turnstile.failed ? (
                <p className="text-xs text-destructive">
                  A verificação de segurança não carregou. Recarregue a página ou chame no WhatsApp.
                </p>
              ) : (
                turnstile.isReady &&
                !turnstile.token && (
                  <p className="text-xs text-muted-foreground">Confirme que você não é robô.</p>
                )
              )}
            </div>

            <button
              type="submit"
              disabled={createLead.isPending || !lgpdConsent}
              className="inline-flex h-[50px] items-center justify-center gap-2 rounded-[13px] bg-primary text-[15px] font-bold text-primary-foreground transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createLead.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createLead.isPending ? "Enviando…" : "Agendar meu diagnóstico"}
            </button>
            <span className="text-center text-[11.5px] text-muted-foreground">
              Resposta em até 1 dia útil. Sem spam, sem lista de e-mail.
            </span>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactNew;
